/**
 * Application-wide z-index tier scale — single source of truth for stacking.
 *
 * Every UI primitive — nav, dialogs, dropdowns, tooltips, toasts — sits in
 * exactly one tier. Inside a tier, instances order themselves via a per-tier
 * counter (e.g., useDialogZIndex bumps within DIALOG when a new dialog opens).
 *
 * This module is also the source of truth for the `--z-*` CSS custom
 * properties — see `installZIndexCssVariables()` which writes them onto
 * `:root` at boot. CSS files consume the vars (`z-index: var(--z-modal)`),
 * TS files consume the constants directly (`Z_TIER.DIALOG`), and both
 * resolve to the same numbers.
 *
 * Why tiers (and not raw numbers):
 *   - Self-documenting: `Z_TIER.OVERLAY` is greppable; `2000` is not.
 *   - Insertion-friendly: gaps between tiers leave room for new layers
 *     without renumbering existing ones.
 *
 * Ordering rationale (low → high):
 *   - NAV sits below DIALOG so navigation chrome never floats above modals.
 *   - FAB sits between NAV and DIALOG — above page chrome, below modals.
 *   - DIALOG_BACKDROP sits just below DIALOG (scrim behind the dialog).
 *   - DIALOG sits below OVERLAY because dropdowns/menus open from inside
 *     dialogs and must float above them.
 *   - OVERLAY hosts floating UI (dropdowns, popovers, hovercards,
 *     autocomplete, command menus).
 *   - TOOLTIP sits above OVERLAY — tooltips on dropdown items must read.
 *   - TOAST sits above TOOLTIP so notifications stay visible regardless of
 *     what floating UI is open.
 *   - TOP is reserved for system overlays (error boundaries, debug panels).
 *
 * Sourced from bgh-katalyst; extended here to absorb the CSS scale.
 */
export const Z_TIER = {
    /** Ordinary page content. Default stacking context. */
    BASE: 0,
    /** Sticky chrome inside scrolling regions — sticky table headers,
     *  sticky save bars. Stays above content but below all nav chrome. */
    STICKY: 100,

    /** Anchor for the NAV tier. */
    NAV: 500,
    /** Backdrops / scrims behind nav-anchored UI. */
    NAV_OVERLAY: 501,
    /** Drawer base on desktop. */
    NAV_DRAWER: 504,
    /** Drawer on mobile / fixed-position drawer. */
    NAV_DRAWER_MOBILE: 505,
    /** Sticky drag-and-drop banner that sits below the app bar but above
     *  the drawer. */
    NAV_DND_BANNER: 509,
    /** App bar. */
    NAV_APP_BAR: 510,
    /** App-bar dropdown menus — Notification, Messages, Settings,
     *  VoidRequests, SearchBar. */
    NAV_APP_BAR_MENU: 511,
    /** ModuleSelector dropdown panel. */
    NAV_SELECTOR: 520,
    /** ModuleSelector trigger when expanded — must float above the
     *  dropdown panel. */
    NAV_SELECTOR_TRIGGER: 521,
    /** Navigation flyout in rail mode — appears beside the rail and must
     *  float above the app bar. */
    NAV_FLYOUT: 530,

    /** Floating action buttons — above page chrome, below modals. */
    FAB: 700,

    /** Scrim / backdrop behind a modal dialog or sheet. */
    DIALOG_BACKDROP: 999,
    /** Modal dialogs and side-sheets opened in front of the page. */
    DIALOG: 1000,
    /** Floating UI that opens from within a dialog or page — dropdowns,
     *  popovers, hovercards, autocomplete, menus, command palettes. */
    OVERLAY: 2000,
    /** Tooltips — above other floating UI so they remain readable. */
    TOOLTIP: 2500,
    /** Toast / snackbar notifications — always on top of interactive UI. */
    TOAST: 3000,
    /** Critical / system overlays — error boundaries, debug panels. */
    TOP: 9000,
} as const;

export type ZTier = keyof typeof Z_TIER;

/**
 * Per-tier instance capacity. With this cap, a tier supports up to 999
 * stacked instances before colliding with the next tier.
 */
export const TIER_CAPACITY = 999;

/**
 * Mapping from `--z-*` CSS custom property name → Z_TIER slot.
 *
 * Used by `installZIndexCssVariables()` to write the entire scale onto
 * `:root` at boot. To add or rename a CSS variable, edit this map.
 */
const CSS_VAR_MAP: Record<string, number> = {
    '--z-sticky': Z_TIER.STICKY,
    '--z-fab': Z_TIER.FAB,
    '--z-dropdown': Z_TIER.OVERLAY,
    '--z-popover': Z_TIER.OVERLAY,
    '--z-modal-backdrop': Z_TIER.DIALOG_BACKDROP,
    '--z-modal': Z_TIER.DIALOG,
    '--z-tooltip': Z_TIER.TOOLTIP,
    '--z-toast': Z_TIER.TOAST,
};

/**
 * Write the `--z-*` scale onto `document.documentElement` so CSS rules can
 * consume them via `var(--z-modal)` etc. Call once at app boot, before
 * mount, so the first paint already sees the values.
 *
 * Idempotent — safe to call multiple times.
 */
export function installZIndexCssVariables(): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    for (const [name, value] of Object.entries(CSS_VAR_MAP)) {
        root.style.setProperty(name, String(value));
    }
}
